import React, { useState, useEffect } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Translate, getSortState } from 'react-jhipster';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { getEntities } from './scheduling-history.reducer';
import { ASC, DESC, DEFAULT_ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { Box } from '@mui/material';
import { PageHeader } from 'app/shared/components/page-header/PageHeader';
import { TableComponent } from 'app/shared/components/table-component/TableComponent';
import { Loading } from 'app/shared/util/loading';
import { PaginationComponent } from 'app/shared/components/pagination/Pagination';
import { NoData } from 'app/shared/components/no-data/NoData';
import { IHeaderItem } from 'app/shared/interfaces/headerItemsInterface';
import { IListData } from 'app/shared/interfaces/listDataInterface';
import { convertDateTimeToPtBR } from 'app/shared/util/date-utils';

import { SchedulingHistoryDetailModal } from './scheduling-history-modal';

export const SchedulingHistory = (props: RouteComponentProps<{ url: string }>) => {
  const dispatch = useAppDispatch();
  const schedulingHistoryList = useAppSelector(state => state.schedulingHistory.entities);
  const loading = useAppSelector(state => state.schedulingHistory.loading);
  const totalItems = useAppSelector(state => state.schedulingHistory.totalItems);
  const { match } = props;

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(props.location, DEFAULT_ITEMS_PER_PAGE, 'id'), props.location.search)
  );
  const [modalOpen, setModalOpen] = useState(false);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      })
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (props.location.search !== endURL) {
      props.history.push(`${props.location.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(props.location.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [props.location.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const formattedSchedulingHistoryList = schedulingHistoryList.map(schedulingHistory => ({
    ...schedulingHistory,
    created: convertDateTimeToPtBR(schedulingHistory.created),
    scheduling: schedulingHistory.scheduling
      ? {
          ...schedulingHistory.scheduling,
          startTime: schedulingHistory.scheduling.startTime ? convertDateTimeToPtBR(schedulingHistory.scheduling.startTime) : null,
          endTime: schedulingHistory.scheduling.endTime ? convertDateTimeToPtBR(schedulingHistory.scheduling.endTime) : null,
        }
      : null,
  }));

  const groupButtonIcons = [
    {
      icon: <VisibilityIcon color="secondary" />,
      path: '',
    },
  ];

  const headers: IHeaderItem[] = [
    { key: 'created', sortable: true, component: <Translate contentKey="fleetSenseApp.schedulingHistory.created" /> },
    { key: 'schedulingId', sortable: false, component: <Translate contentKey="fleetSenseApp.scheduling.id" /> },
    { key: 'schedulingDescription', sortable: false, component: <Translate contentKey="fleetSenseApp.scheduling.description" /> },
    { key: 'schedulingStartTime', sortable: true, component: <Translate contentKey="fleetSenseApp.scheduling.startTime" /> },
    { key: 'schedulingEndTime', sortable: true, component: <Translate contentKey="fleetSenseApp.scheduling.endTime" /> },
    { key: 'schedulingStatus', sortable: true, component: <Translate contentKey="fleetSenseApp.scheduling.status" /> },
  ];

  const data: IListData[] = [
    { type: 'text', value: 'created' },
    { type: 'text', value: 'scheduling?.id' },
    { type: 'text', value: 'scheduling?.description' },
    { type: 'text', value: 'scheduling?.startTime' },
    { type: 'text', value: 'scheduling?.endTime' },
    { type: 'translateText', value: 'scheduling?.status', translate: 'fleetSenseApp.SchedulingStatus' },
    { type: 'viewButton' },
  ];

  return (
    <Box sx={{ backgroundColor: 'primary.main', borderRadius: '8px', paddingTop: '16px', textAlign: 'center', padding: '24px' }}>
      <PageHeader
        headingId="inspection-heading"
        headingDataCy="inspectionHeading"
        contentKey="fleetSenseApp.schedulingHistory.home.title"
        buttonTextKey="fleetSenseApp.schedulingHistory.home.createLabel"
        showButton={false}
        match={match}
      />
      {!loading && !!schedulingHistoryList?.length && (
        <TableComponent
          listData={data}
          headers={headers}
          list={formattedSchedulingHistoryList}
          match={match}
          sortFunction={sort}
          pagination={paginationState}
          groupButtonIcons={groupButtonIcons}
          setSelect={setModalOpen}
        />
      )}
      {modalOpen && <SchedulingHistoryDetailModal props={props} modalOpen={modalOpen} setModalOpen={setModalOpen}></SchedulingHistoryDetailModal>}
      {!loading && !schedulingHistoryList.length && <NoData />}
      {loading && <Loading />}
      <PaginationComponent paginationState={paginationState} totalItems={totalItems} onChange={handlePagination} list={schedulingHistoryList} />
    </Box>
  );
};
